/**
 * Le bandeau qui dit ce que porteront les notes saisies ensuite.
 *
 * Purement présentatif : l'état et les règles de cascade vivent dans `usePorteeDeNote`. Le
 * séparer ainsi permet à l'import de réutiliser les mêmes règles tout en affichant, à côté,
 * ce qui ne concerne que lui.
 */

import React from 'react';
import { Info } from 'lucide-react';
import { NOTE_TOPICS } from '../../constants/noteTopics';
import { grouperComptes } from '../../constants/plateformes';
import { useBulleAuSurvol } from '../BulleSurvol';

/**
 * Texte de l'infobulle de « Afficher sur les graphiques ».
 *
 * ⚠️ **Un paragraphe par entrée, jamais une ligne.** La bulle rend le texte en `pre-line` et
 * respecte donc les retours à la ligne écrits ici : les coupures qui servaient l'infobulle
 * native, qui ne sait pas replier, tombaient au milieu des phrases une fois la bulle maison
 * en place. La largeur du repli est l'affaire de la bulle.
 */
export const INFOBULLE_GRAPHIQUES = [
  'Cochée : la note devient un repère daté sur les courbes de performance du dashboard. On voit sur quel jour elle tombe, et ce que les chiffres faisaient alors.',
  '',
  'Décochée : elle reste enregistrée, et consultable dans l\'archive comme dans le calendrier. Elle n\'apparaît simplement pas sur les courbes.',
  '',
  // Vérifié dans le code avant d'être promis ici : le MCP expose `list_notes`, `get_note` et
  // `get_notes_stats` (lecture seule), et l'analyse du dashboard consolidé joint les notes de
  // la période aux données envoyées au modèle. Les dashboards par régie, eux, ne les
  // transmettent pas — d'où « depuis le dashboard consolidé », et non « aux analyses ».
  'Dans les deux cas, la note nourrit l\'IA : le MCP sait la lire et la rechercher, et une analyse lancée depuis le dashboard consolidé la transmet au modèle avec les chiffres de la période.'
].join('\n');

export const STYLE_SELECTEUR = {
  padding: '8px 10px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '13px',
  backgroundColor: 'white',
  cursor: 'pointer'
};

const PorteeDeNote = ({ portee, clients = [], intro = 'Les notes saisies porteront :', children }) => {
  // La bulle maison plutôt que l'attribut `title` : cinq lignes d'explication dans une
  // infobulle native s'affichent au bon vouloir du système — et ne s'affichaient pas du tout
  // sur l'écran d'import. Celle-ci est du HTML, elle sort par un portail et respecte les
  // retours à la ligne.
  const { survoler, quitter, bulle } = useBulleAuSurvol();

  const {
    clientId, siteId, accountId, sujet, surGraphique,
    setClientId, setSiteId, setAccountId, setSujet, setSurGraphique,
    sitesDuClient, comptesDuPerimetre
  } = portee;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
      padding: '12px 16px', backgroundColor: '#FAF9F5', border: '1px solid #e5e7eb',
      borderRadius: '8px', marginBottom: '16px'
    }}>
      <span style={{ fontSize: '13px', color: '#6b7280' }}>{intro}</span>

      <select
        value={clientId}
        onChange={e => { setClientId(e.target.value); setSiteId(''); }}
        style={STYLE_SELECTEUR}
      >
        <option value="">Tous les clients</option>
        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      <select value={siteId} onChange={e => setSiteId(e.target.value)} style={STYLE_SELECTEUR}>
        <option value="">Tous les sites</option>
        {sitesDuClient.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>

      <select value={accountId} onChange={e => setAccountId(e.target.value)} style={STYLE_SELECTEUR}>
        <option value="">Tous les comptes</option>
        {/* Regroupés par plateforme : la liste mêle régies et solutions de mesure, et rien
            dans un nom de compte ne dit d'où il vient. */}
        {grouperComptes(comptesDuPerimetre).map(groupe => (
          <optgroup key={groupe.type} label={groupe.libelle}>
            {groupe.comptes.map(a => <option key={a.sourceId} value={a.sourceId}>{a.name}</option>)}
          </optgroup>
        ))}
      </select>

      {/*
        La valeur vide porte deux sens, comme celles de Client et de Site juste avant : à la
        saisie, la note part sans sujet ; à l'affichage, aucun sujet n'est exclu. « Sujet »
        dit ce que le sélecteur règle, là où « Aucun sujet » annonçait une valeur et laissait
        croire que le calendrier ne montrerait que les notes non classées.
      */}
      <select value={sujet} onChange={e => setSujet(e.target.value)} style={STYLE_SELECTEUR}>
        <option value="">Sujet</option>
        {NOTE_TOPICS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>

      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
        <input type="checkbox" checked={surGraphique} onChange={e => setSurGraphique(e.target.checked)} />
        Afficher sur les graphiques
        {/* Le survol est écouté sur le `<span>`, pas sur l'icône : lucide rend un `<svg>`, et
            la zone utile d'une icône de treize pixels est trop petite pour viser juste. */}
        <span
          onMouseEnter={survoler(INFOBULLE_GRAPHIQUES)}
          onMouseLeave={quitter}
          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help', padding: '2px' }}
        >
          <Info size={13} style={{ color: '#94a3b8' }} />
        </span>
      </label>

      {children}
      {/* Sa place dans l'arbre est sans effet : la bulle sort par un portail. */}
      {bulle}
    </div>
  );
};

export default PorteeDeNote;
